import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Search, RefreshCw, Users, ExternalLink, Calendar, User, Building, Target, Clock, MessageSquare, Briefcase, BarChart3, Table, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { SURVEY_STATUS_OPTIONS } from '../../constants/survey';
import SurveyModal from './SurveyModal';
import SurveyDashboard from './SurveyDashboard';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

interface SurveyResponse {
  id: string;
  user_id?: string;
  name?: string;
  email?: string | null;
  linkedin_profile: string;
  linkedin_full_url?: string;
  duration: string;
  brand_vibe: string;
  client_acquisition: string;
  client_count: string;
  long_term: string;
  marketing_time: string;
  additional_notes: string;
  status: string;
  created_at: string;
  submitted_at?: string;
}

const SurveyView: React.FC = () => {
  const { user } = useAuth();
  const [responses, setResponses] = useState<SurveyResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentResponse, setCurrentResponse] = useState<SurveyResponse | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'dashboard' | 'responses'>('dashboard');

  const fetchResponses = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('cto_survey')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setResponses(data || []);
    } catch (error: any) {
      toast.error(`Error fetching survey responses: ${error.message}`);
      setResponses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResponses();

    // Set up realtime subscription
    const channel = supabase
      .channel('cto_survey_changes')
      .on('subscribe', (status, err) => {
        console.log('Realtime subscription status:', status);
        if (err) {
          console.error('Realtime subscription error:', err);
          toast.error('Failed to connect to realtime updates');
        } else if (status === 'SUBSCRIBED') {
          console.log('Successfully subscribed to realtime updates');
          toast.success('Connected to live updates');
        }
      })
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'cto_survey'
        },
        (payload: RealtimePostgresChangesPayload<SurveyResponse>) => {
          console.log('Realtime update:', payload);
          
          if (payload.eventType === 'INSERT') {
            setResponses(prev => [payload.new, ...prev]);
            toast.success('New survey response received!');
          } else if (payload.eventType === 'UPDATE') {
            setResponses(prev => 
              prev.map(response => 
                response.id === payload.new.id ? payload.new : response
              )
            );
            // Only show toast if we're not the one making the update
            if (!isModalOpen) {
              toast.info('Survey response updated');
            }
          } else if (payload.eventType === 'DELETE') {
            setResponses(prev => 
              prev.filter(response => response.id !== payload.old.id)
            );
            toast.info('Survey response deleted');
          }
        }
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      console.log('Cleaning up realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [isModalOpen]);

  const filteredResponses = responses.filter(response => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = (
      response.name?.toLowerCase().includes(searchLower) ||
      response.email?.toLowerCase().includes(searchLower) ||
      response.linkedin_profile.toLowerCase().includes(searchLower) ||
      response.brand_vibe.toLowerCase().includes(searchLower) ||
      response.client_acquisition.toLowerCase().includes(searchLower) ||
      response.additional_notes.toLowerCase().includes(searchLower)
    );
    
    const matchesStatus = statusFilter === 'all' || response.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleViewResponse = (response: SurveyResponse) => {
    setCurrentResponse(response);
    setIsModalOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'new':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'submitted':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'completed by user':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300';
      case 'closed':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const formatDate = (date: string) => {
    const dateObj = new Date(date);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - dateObj.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    const formattedDate = dateObj.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
    
    let ageText = '';
    if (diffDays === 1) {
      ageText = '1 day ago';
    } else if (diffDays < 30) {
      ageText = `${diffDays} days ago`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      ageText = months === 1 ? '1 month ago' : `${months} months ago`;
    } else {
      const years = Math.floor(diffDays / 365);
      ageText = years === 1 ? '1 year ago' : `${years} years ago`;
    }
    
    return { formattedDate, ageText };
  };

  const handleLinkedInClick = (e: React.MouseEvent, response: SurveyResponse) => {
    e.stopPropagation();
    const url = response.linkedin_full_url || `https://linkedin.com/in/${response.linkedin_profile}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    ...Object.entries(SURVEY_STATUS_OPTIONS).map(([key, label]) => ({
      value: key,
      label
    }))
  ];

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <Users className="w-8 h-8 mr-3 text-emerald-500" />
            CTO Survey (legacy)
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Summer 2025 fractional CTO survey — data from the <code className="text-sm">cto_survey</code> table
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'dashboard'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('responses')}
              className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'responses'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Table className="w-4 h-4 mr-2" />
              Responses
            </button>
          </div>
          {activeTab === 'responses' && (
            <>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search responses..."
                  className="pl-10 pr-4 py-2.5 w-64 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-shadow"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <button
                onClick={() => window.open('https://questions.stephansmith.solutions/admin', '_blank', 'noopener,noreferrer')}
                className="flex items-center px-4 py-2.5 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
              >
                <Plus className="w-5 h-5 mr-2" />
                Invite
              </button>
            </>
          )}
        </div>
      </div>

      {activeTab === 'dashboard' ? (
        <SurveyDashboard responses={responses} />
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Business Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center">
                      <RefreshCw className="w-6 h-6 animate-spin text-emerald-500 mx-auto" />
                    </td>
                  </tr>
                ) : filteredResponses.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                      No survey responses found
                    </td>
                  </tr>
                ) : (
                  filteredResponses.map((response) => (
                    <tr
                      key={response.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      onClick={() => handleViewResponse(response)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {response.name || 'Anonymous'}
                            </div>
                            {response.email ? (
                              <div className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">
                                {response.email}
                              </div>
                            ) : (
                              <div className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                                No email yet
                              </div>
                            )}
                            <button
                              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center mt-1"
                              onClick={(e) => handleLinkedInClick(e, response)}
                            >
                              {response.linkedin_profile}
                              <ExternalLink className="w-3 h-3 ml-1" />
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white">
                          <div className="flex items-center mb-1">
                            <Clock className="w-4 h-4 mr-1 text-gray-400" />
                            {response.duration}
                          </div>
                          <div className="flex items-center">
                            <Users className="w-4 h-4 mr-1 text-gray-400" />
                            {response.client_count} clients
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(response.status)}`}>
                          {response.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white">
                          <div>
                            {response.status === 'submitted' && response.submitted_at ? 'Submitted: ' : 'Created: '}
                            {response.status === 'submitted' && response.submitted_at 
                              ? formatDate(response.submitted_at).formattedDate 
                              : formatDate(response.created_at).formattedDate}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {response.status === 'submitted' && response.submitted_at 
                              ? formatDate(response.submitted_at).ageText 
                              : formatDate(response.created_at).ageText}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewResponse(response);
                          }}
                          className="text-sm text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SurveyModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        response={currentResponse}
        onSuccess={fetchResponses}
      />
    </div>
  );
};

export default SurveyView;